"use client";

import { createContext, useContext, type ReactNode } from "react";
import {
  useCartStore,
  useCateringCartStore,
  type CartStoreHook,
} from "@/stores/cart-store";

export type CartScope = "meal" | "catering";

const CartScopeContext = createContext<CartScope>("meal");

/**
 * Which persisted cart the ordering components under this provider read and
 * write. `/order/catering/**` runs under `catering`; everything else under
 * `/order` stays on the meal cart. Components that add items or render the
 * cart resolve the store through `useActiveCartStore()` instead of importing
 * `useCartStore` directly, so one card/sheet implementation serves both
 * carts without ever mixing their contents.
 */
export function CartScopeProvider({
  scope,
  children,
}: {
  scope: CartScope;
  children: ReactNode;
}) {
  return (
    <CartScopeContext.Provider value={scope}>{children}</CartScopeContext.Provider>
  );
}

export function useCartScope(): CartScope {
  return useContext(CartScopeContext);
}

export function useActiveCartStore(): CartStoreHook {
  return useCartScope() === "catering" ? useCateringCartStore : useCartStore;
}

export function cartScopeForPathname(pathname: string | null): CartScope {
  return pathname?.startsWith("/order/catering") ? "catering" : "meal";
}

export function checkoutPathForScope(scope: CartScope): string {
  return scope === "catering" ? "/order/catering/checkout" : "/order/checkout";
}

export function browsePathForScope(scope: CartScope): string {
  return scope === "catering" ? "/order/catering" : "/order";
}
